import { DSDropzone } from "@components/design-system/molecules";
import { faFilePdf } from "@fortawesome/pro-solid-svg-icons";
import React from "react";

const FileUploader = ({
  onFileUpload,
  highlightFileUploader,
  onboardingMode,
  label = "Upload your template",
  helperText = "10MB limit, PDFs only",
  disabled = false,
  dataTestId = "file-uploader",
}) => {
  const [uploadedFileName, setUploadedFileName] =
    React.useState(null);

  const handleFilesSelected = (files) => {
    const file = files[0];
    if (file?.type === "application/pdf") {
      setUploadedFileName(file.name);
      onFileUpload(file);
    }
  };

  if (onboardingMode) {
    // Onboarding mode retains custom styling since DSDropzone doesn't support transparent/white-text variants
    return (
      <OnboardingFileUploader
        onFileUpload={onFileUpload}
        highlightFileUploader={highlightFileUploader}
      />
    );
  }

  return (
    <div className="mb-4">
      <DSDropzone
        accept="application/pdf"
        icon={faFilePdf}
        label={label}
        helperText={helperText}
        onFilesSelected={handleFilesSelected}
        selectedFiles={
          uploadedFileName ? [{ name: uploadedFileName }] : undefined
        }
        onClear={() => setUploadedFileName(null)}
        disabled={disabled}
        dataTestId={dataTestId}
      />
    </div>
  );
};

// Keep custom onboarding uploader since it has unique transparent/white styling
const OnboardingFileUploader = ({
  onFileUpload,
  highlightFileUploader,
}) => {
  const fileInputRef = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const openFileExplorer = () => {
    fileInputRef.current.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file?.type === "application/pdf") {
      onFileUpload(file);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file?.type === "application/pdf") {
      onFileUpload(file);
    }
  };

  return (
    <div
      className={`mt-1 flex justify-center px-6 py-12 my-2 rounded-large cursor-pointer transition-all duration-300 mb-md border-2 border-dashed
        ${
          isDragging
            ? "border-indigo-300 bg-indigo-500 cursor-copy shadow-lg"
            : highlightFileUploader
              ? "border-indigo-300 bg-indigo-500 shadow-lg"
              : "border-indigo-300 bg-transparent shadow-lg"
        }`}
      onClick={openFileExplorer}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      data-testid="file-uploader"
    >
      <div className="space-y-1 text-center text-white">
        <span className="font-medium">
          {isDragging ? "Drop" : "Upload"}
        </span>
        <p className="pl-1 text-sm">your template</p>
        <p className="text-xs">10MB limit, PDFs only</p>
      </div>
      <input
        ref={fileInputRef}
        id="file-upload"
        name="template"
        type="file"
        accept="application/pdf"
        className="sr-only"
        onChange={handleChange}
        data-testid="file-upload-input"
      />
    </div>
  );
};

export default FileUploader;
